import type React from 'react'
import { flowTriggerLabel } from '../../../../../types/loyaltyMarketing'
import {
  FONT,
  R,
  T,
  formError,
  formHint,
  formInput,
  formLabel,
  formSelect,
} from '../builderStyles'
import {
  type CampaignDraft,
  type DraftReminder,
  MAX_REMINDERS,
  REMINDER_SUBJECTS,
  emailOneText,
  newReminder,
  toDatetimeLocalValue,
} from '../useCampaignDraft'
import {
  CheckSvg,
  LockedFieldset,
  NoteBanner,
  RadioOpt,
  SectionLabel,
  Stack,
  StepCard,
} from './stepUi'

// Step 5 (prototype L2468–2575). Email: Send immediately / Schedule for
// later (Smart send time is hidden; a loaded smart campaign keeps its mode
// with a read-only note) + Campaign ends. Abandoned cart (campaign builder
// v2, renderCartSchedule): 1 Trigger Event, 2 Trigger Reminder Email,
// 3 Exclusions, 4 Frequency Limits. Older event-triggered flows keep a
// read-only trigger, the wait and the frequency limit.

// Abandoned-cart "Wait before sending" choices. Plain values are hours; the
// "m" suffix marks a minutes value in the select.
export const WAIT_OPTIONS: { value: string; label: string }[] = [
  { value: '15m', label: '15 minutes' },
  { value: '1', label: '1 hour (recommended)' },
  { value: '6', label: '6 hours' },
  { value: '24', label: '24 hours' },
  { value: '48', label: '48 hours' },
]

// Non-production only: a 1-minute wait so abandoned-cart flows can be tested
// without waiting.
const TEST_WAIT_OPTION = {
  value: '1m',
  label: '1 minute (testing)',
}
const IS_PRODUCTION = import.meta.env.VITE_REACT_APP_ENV === 'production'
const waitOptions = IS_PRODUCTION
  ? WAIT_OPTIONS
  : [TEST_WAIT_OPTION, ...WAIT_OPTIONS]

// Reminder "Resend after" choices after the previous email: hours, or
// minutes with an "m" suffix.
export const RESEND_OPTIONS: { value: string; label: string }[] = [
  { value: '24', label: '24 hours' },
  { value: '48', label: '48 hours' },
  { value: '72', label: '72 hours' },
]

// Non-production only: a 5-minute resend so reminder sequences (e.g. the
// 3-Step Sequence) can be tested without waiting a day per email.
const TEST_RESEND_OPTION = { value: '5m', label: '5 minutes (testing)' }
const resendOptions = IS_PRODUCTION
  ? RESEND_OPTIONS
  : [TEST_RESEND_OPTION, ...RESEND_OPTIONS]

// "Let the customer re-enter this flow" — the flow's frequency limit
// (resend_cooldown_days). '0' = every time.
export const COOLDOWN_OPTIONS: { value: string; label: string }[] = [
  { value: '0', label: 'Every time they abandon a cart' },
  { value: '1', label: 'At most once a day' },
  { value: '3', label: 'At most once every 3 days' },
  { value: '7', label: 'At most once every 7 days (recommended)' },
  { value: '30', label: 'At most once every 30 days' },
]

export const MIN_CART_OPTIONS: { value: string; label: string }[] = [
  { value: '0', label: 'Any cart value' },
  { value: '20', label: 'Over $20' },
  { value: '50', label: 'Over $50' },
  { value: '100', label: 'Over $100' },
]

// "1 hour", "15 min", "2 days 3 hours".
export const formatWait = (value: string, unit: string): string => {
  const n = Number.parseInt(value, 10) || 0
  const minutes = unit === 'minutes' ? n : unit === 'days' ? n * 1440 : n * 60
  if (minutes < 60) return `${minutes} min`
  const d = Math.floor(minutes / 1440)
  const h = Math.floor((minutes % 1440) / 60)
  const m = minutes % 60
  const parts: string[] = []
  if (d) parts.push(`${d} ${d === 1 ? 'day' : 'days'}`)
  if (h) parts.push(`${h} ${h === 1 ? 'hour' : 'hours'}`)
  if (m) parts.push(`${m} min`)
  return parts.join(' ')
}

const DEFAULT_SEND_TIME = '10:00'

// datetime-local value from the date/time inputs; a cleared time falls back
// to 10:00 and a missing date to today.
export const composeScheduledAt = (date: string, time: string): string =>
  `${date || toDatetimeLocalValue(new Date().toISOString()).split('T')[0]}T${
    time || DEFAULT_SEND_TIME
  }`

// "PDT", "EST", "GMT+2"… for the browser's timezone.
export const timeZoneAbbr = (date = new Date()): string => {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    return (
      new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
        .formatToParts(date)
        .find((p) => p.type === 'timeZoneName')?.value ?? timeZone
    )
  } catch {
    return 'local'
  }
}

// Numbered card title badge (prototype `.cs-num`).
const Num: React.FC<{ n: number }> = ({ n }) => (
  <span
    style={{
      display: 'inline-grid',
      placeItems: 'center',
      width: 20,
      height: 20,
      borderRadius: '50%',
      background: T.gray800,
      color: T.white,
      fontSize: 11,
      fontWeight: 700,
    }}
  >
    {n}
  </span>
)

// Prototype `.cs-switch`.
const Switch: React.FC<{
  on: boolean
  label: string
  onToggle: () => void
}> = ({ on, label, onToggle }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    onClick={onToggle}
    style={{
      flexShrink: 0,
      width: 40,
      height: 22,
      borderRadius: 11,
      border: 'none',
      padding: 0,
      background: on ? T.green : T.gray300,
      position: 'relative',
      cursor: 'pointer',
      transition: 'background .2s',
    }}
  >
    <span
      style={{
        position: 'absolute',
        top: 2,
        left: on ? 20 : 2,
        width: 18,
        height: 18,
        borderRadius: '50%',
        background: T.white,
        boxShadow: '0 1px 2px rgba(0,0,0,.2)',
        transition: 'left .2s',
      }}
    />
  </button>
)

// A select that also shows a loaded value outside its options.
const OptionSelect: React.FC<{
  id: string
  value: string
  options: { value: string; label: string }[]
  customLabel: string
  onChange: (value: string) => void
  style?: React.CSSProperties
}> = ({ id, value, options, customLabel, onChange, style }) => (
  <select
    id={id}
    style={{ ...formSelect, ...style }}
    value={value}
    onChange={(e) => onChange(e.target.value)}
  >
    {!options.some((o) => o.value === value) && (
      <option value={value}>{customLabel}</option>
    )}
    {options.map((o) => (
      <option key={o.value} value={o.value}>
        {o.label}
      </option>
    ))}
  </select>
)

const textarea: React.CSSProperties = {
  ...formInput,
  minHeight: 56,
  resize: 'vertical',
  fontFamily: FONT,
}
const readOnly: React.CSSProperties = {
  background: T.gray100,
  color: T.gray500,
  cursor: 'not-allowed',
}

// Prototype `.cs-email`: one email row of the reminder card.
const EmailRow: React.FC<{
  tag: string
  first?: boolean
  focused: boolean
  onFocus: () => void
  onRemove?: () => void
  children: React.ReactNode
}> = ({ tag, first, focused, onFocus, onRemove, children }) => (
  <div
    onClick={onFocus}
    onFocus={onFocus}
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
      padding: 12,
      border: `1.5px ${first ? 'dashed' : 'solid'} ${
        focused ? T.blue : T.gray200
      }`,
      borderRadius: R.md,
      background: first ? T.gray50 : T.white,
      boxShadow: focused ? `0 0 0 3px ${T.blueBg}` : 'none',
      opacity: first && !focused ? 0.75 : 1,
      cursor: 'pointer',
      transition: 'border-color .15s, box-shadow .15s',
    }}
  >
    <div
      style={{
        flexShrink: 0,
        marginTop: 24,
        fontSize: 12,
        fontWeight: 700,
        color: first ? T.gray500 : T.blue,
        background: first ? T.gray200 : T.blueBg,
        padding: '6px 10px',
        borderRadius: R.sm,
        minWidth: 68,
        textAlign: 'center',
      }}
    >
      {tag}
    </div>
    <div
      style={{
        flex: 1,
        minWidth: 0,
        display: 'grid',
        gridTemplateColumns: '1fr 180px',
        gap: 10,
      }}
    >
      {children}
    </div>
    {onRemove ? (
      <button
        type="button"
        title={`Remove ${tag}`}
        aria-label={`Remove ${tag}`}
        onClick={(e) => {
          e.stopPropagation()
          onRemove()
        }}
        style={{
          flexShrink: 0,
          marginTop: 24,
          width: 34,
          height: 38,
          borderRadius: R.sm,
          border: `1.5px solid ${T.gray200}`,
          color: T.gray400,
          background: T.white,
          display: 'grid',
          placeItems: 'center',
          cursor: 'pointer',
        }}
      >
        <svg
          width={15}
          height={15}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24"
          aria-hidden
        >
          <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" />
        </svg>
      </button>
    ) : (
      <div style={{ flexShrink: 0, width: 34 }} />
    )}
  </div>
)

const span: React.CSSProperties = { gridColumn: '1 / -1' }

// Prototype `.cs-check` (Exclusions).
const ExitCheck: React.FC<{
  on: boolean
  locked?: boolean
  title: string
  desc: string
  onToggle?: () => void
}> = ({ on, locked, title, desc, onToggle }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={on}
    aria-disabled={locked}
    onClick={locked ? undefined : onToggle}
    style={{
      display: 'flex',
      gap: 10,
      alignItems: 'flex-start',
      padding: '10px 12px',
      border: `1.5px solid ${on ? T.greenMid : T.gray200}`,
      borderRadius: R.sm,
      background: on ? '#F6FBF8' : T.white,
      cursor: locked ? 'default' : 'pointer',
      textAlign: 'left',
      width: '100%',
      fontFamily: FONT,
    }}
  >
    <span
      style={{
        width: 17,
        height: 17,
        borderRadius: 4,
        border: `1.5px solid ${
          on ? (locked ? T.gray400 : T.green) : T.gray300
        }`,
        background: on ? (locked ? T.gray400 : T.green) : T.white,
        color: T.white,
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
        marginTop: 1,
        boxSizing: 'border-box',
      }}
    >
      {on && <CheckSvg size={11} />}
    </span>
    <span>
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 13,
          fontWeight: 600,
          color: T.gray800,
          flexWrap: 'wrap',
        }}
      >
        {title}
        {locked && (
          <span
            style={{
              fontSize: 9.5,
              fontWeight: 700,
              color: T.gray500,
              background: T.gray100,
              padding: '1px 6px',
              borderRadius: 8,
            }}
          >
            Always on
          </span>
        )}
      </span>
      <span
        style={{
          display: 'block',
          fontSize: 11.5,
          color: T.gray500,
          marginTop: 2,
          lineHeight: 1.4,
        }}
      >
        {desc}
      </span>
    </span>
  </button>
)

export interface ScheduleStepProps {
  draft: CampaignDraft
  isFlow: boolean
  scheduleLocked: boolean
  scheduledAtValid: boolean
  endsAtValid: boolean
  // Abandoned cart: the email the preview shows (-1 = Email 1, i = reminder i).
  focusedEmail?: number
  onFocusEmail?: (index: number) => void
  // Abandoned cart: the "Trigger Reminder Email" card (bs-enable-campaign-
  // uplift). Hidden = Email 1 only; the cards after it renumber.
  showReminders?: boolean
}

export const ScheduleStep: React.FC<ScheduleStepProps> = ({
  draft,
  isFlow,
  scheduleLocked,
  scheduledAtValid,
  endsAtValid,
  focusedEmail = -1,
  onFocusEmail = () => {},
  showReminders = true,
}) => {
  const { state, set, patch } = draft
  const waitValue =
    state.delayUnit === 'minutes' ? `${state.delayValue}m` : state.delayValue
  const [datePart = '', timePart = ''] = state.scheduledAt.split('T')
  const isCart = state.triggerMetric === 'abandoned_cart'
  const waitLabel = formatWait(state.delayValue, state.delayUnit)

  const setReminder = (i: number, change: Partial<DraftReminder>) => {
    const next = state.reminders.map((r, j) => {
      if (j !== i) return r
      const merged = { ...r, ...change }
      if ('headline' in change) merged.headlineLinked = false
      else if ('subject' in change && r.headlineLinked)
        merged.headline = change.subject ?? ''
      return merged
    })
    set('reminders', next)
  }
  const toggleReminders = () => {
    const on = !state.remindersOn
    patch({
      remindersOn: on,
      reminders:
        on && !state.reminders.length ? [newReminder(0)] : state.reminders,
    })
    onFocusEmail(on ? 0 : -1)
  }
  const addReminder = () => {
    if (state.reminders.length >= MAX_REMINDERS) return
    set('reminders', [...state.reminders, newReminder(state.reminders.length)])
    onFocusEmail(state.reminders.length)
  }
  const removeReminder = (i: number) => {
    const next = state.reminders.filter((_, j) => j !== i)
    patch({ reminders: next, remindersOn: next.length > 0 })
    onFocusEmail(next.length ? Math.min(i, next.length - 1) : -1)
  }

  const waitSelect = (
    <div>
      <label style={formLabel} htmlFor="cb-wait">
        Wait before sending
      </label>
      <OptionSelect
        id="cb-wait"
        value={waitValue}
        options={waitOptions}
        customLabel={waitLabel}
        onChange={(v) =>
          patch(
            v.endsWith('m')
              ? { delayValue: v.slice(0, -1), delayUnit: 'minutes' }
              : { delayValue: v, delayUnit: 'hours' },
          )
        }
      />
    </div>
  )

  const frequencyCard = (n?: number) => (
    <StepCard
      title="Frequency Limits"
      icon={n ? <Num n={n} /> : undefined}
      subtitle="Stops repeat abandoners from getting the same reminder over and over"
    >
      <label style={formLabel} htmlFor="cb-cooldown">
        Let the customer re-enter this flow
      </label>
      <OptionSelect
        id="cb-cooldown"
        value={state.cooldownDays}
        options={COOLDOWN_OPTIONS}
        customLabel={`At most once every ${state.cooldownDays} days`}
        onChange={(v) => set('cooldownDays', v)}
        style={{ maxWidth: 360 }}
      />
    </StepCard>
  )

  if (isFlow && !isCart)
    return (
      <Stack>
        <SectionLabel>How should this automation run?</SectionLabel>
        <StepCard title="Trigger Event">
          <Stack>
            <div>
              <label style={formLabel} htmlFor="cb-trigger">
                Fire when
              </label>
              <input
                id="cb-trigger"
                style={{ ...formInput, background: T.gray50, color: T.gray600 }}
                value={flowTriggerLabel(state.triggerMetric)}
                readOnly
                disabled
              />
              <div style={formHint}>
                This automation was created with an earlier trigger — it keeps
                working but the trigger can't be changed here.
              </div>
            </div>
            {waitSelect}
          </Stack>
        </StepCard>
        {frequencyCard()}
      </Stack>
    )

  if (isFlow) {
    const first = emailOneText(state.emailDoc)
    return (
      <Stack>
        <SectionLabel>How should this automation run?</SectionLabel>

        <StepCard title="Trigger Event" icon={<Num n={1} />}>
          <Stack>
            <div>
              <label style={formLabel} htmlFor="cb-trigger">
                Fire when
              </label>
              <select
                id="cb-trigger"
                style={formSelect}
                value={state.cartTrigger}
                onChange={(e) =>
                  set(
                    'cartTrigger',
                    e.target.value === 'checkout' ? 'checkout' : 'cart',
                  )
                }
              >
                <option value="cart">
                  Customer adds items to cart but doesn't place an order
                </option>
                <option value="checkout">
                  Customer initiates checkout but doesn't complete the order
                </option>
              </select>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 12,
              }}
            >
              {waitSelect}
              <div>
                <label style={formLabel} htmlFor="cb-min-cart">
                  Minimum cart value
                </label>
                <OptionSelect
                  id="cb-min-cart"
                  value={state.minCartValue}
                  options={MIN_CART_OPTIONS}
                  customLabel={`Over $${state.minCartValue}`}
                  onChange={(v) => set('minCartValue', v)}
                />
              </div>
            </div>
          </Stack>
        </StepCard>

        {showReminders && (
          <StepCard
            title="Trigger Reminder Email"
            icon={<Num n={2} />}
            subtitle="Send follow-up emails to customers who haven't come back"
            right={
              <Switch
                on={state.remindersOn}
                label="Send reminder emails"
                onToggle={toggleReminders}
              />
            }
          >
            {state.remindersOn ? (
              <Stack gap={10}>
                <EmailRow
                  tag="Email 1"
                  first
                  focused={focusedEmail === -1}
                  onFocus={() => onFocusEmail(-1)}
                >
                  <div>
                    <label style={formLabel} htmlFor="cb-r0-subject">
                      Subject line
                    </label>
                    <input
                      id="cb-r0-subject"
                      style={{ ...formInput, ...readOnly }}
                      value={state.subject || 'Set in the Content step'}
                      disabled
                    />
                  </div>
                  <div>
                    <label style={formLabel} htmlFor="cb-r0-sends">
                      Sends
                    </label>
                    <input
                      id="cb-r0-sends"
                      style={{ ...formInput, ...readOnly }}
                      value={`${waitLabel} after trigger`}
                      disabled
                    />
                  </div>
                  <div style={span}>
                    <label style={formLabel} htmlFor="cb-r0-headline">
                      Headline
                    </label>
                    <input
                      id="cb-r0-headline"
                      style={{ ...formInput, ...readOnly }}
                      value={first.headline}
                      disabled
                    />
                  </div>
                  <div style={span}>
                    <label style={formLabel} htmlFor="cb-r0-desc">
                      Description
                    </label>
                    <textarea
                      id="cb-r0-desc"
                      rows={2}
                      style={{ ...textarea, ...readOnly, resize: 'none' }}
                      value={first.description}
                      disabled
                    />
                  </div>
                </EmailRow>
                <div style={{ ...formHint, marginTop: -4 }}>
                  Email 1 comes from the Content step. Reminders reuse its
                  design with their own subject, headline and description.
                </div>
                {state.reminders.map((r, i) => (
                  <EmailRow
                    key={i}
                    tag={`Email ${i + 2}`}
                    focused={focusedEmail === i}
                    onFocus={() => onFocusEmail(i)}
                    onRemove={() => removeReminder(i)}
                  >
                    <div>
                      <label style={formLabel} htmlFor={`cb-r${i + 1}-subject`}>
                        Subject line
                      </label>
                      <input
                        id={`cb-r${i + 1}-subject`}
                        style={formInput}
                        value={r.subject}
                        placeholder={REMINDER_SUBJECTS[i] ?? 'Reminder subject'}
                        onChange={(e) =>
                          setReminder(i, { subject: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <label style={formLabel} htmlFor={`cb-r${i + 1}-after`}>
                        Resend after Email {i + 1}
                      </label>
                      <OptionSelect
                        id={`cb-r${i + 1}-after`}
                        value={r.resendAfter}
                        options={resendOptions}
                        customLabel={
                          r.resendAfter.endsWith('m')
                            ? formatWait(r.resendAfter.slice(0, -1), 'minutes')
                            : formatWait(r.resendAfter, 'hours')
                        }
                        onChange={(v) => setReminder(i, { resendAfter: v })}
                      />
                    </div>
                    <div style={span}>
                      <label
                        style={formLabel}
                        htmlFor={`cb-r${i + 1}-headline`}
                      >
                        Headline
                      </label>
                      <input
                        id={`cb-r${i + 1}-headline`}
                        style={formInput}
                        value={r.headline}
                        placeholder="Same as subject line"
                        onChange={(e) =>
                          setReminder(i, { headline: e.target.value })
                        }
                      />
                    </div>
                    <div style={span}>
                      <label style={formLabel} htmlFor={`cb-r${i + 1}-desc`}>
                        Description
                      </label>
                      <textarea
                        id={`cb-r${i + 1}-desc`}
                        rows={2}
                        style={textarea}
                        value={r.description}
                        onChange={(e) =>
                          setReminder(i, { description: e.target.value })
                        }
                      />
                    </div>
                  </EmailRow>
                ))}
                {state.reminders.length < MAX_REMINDERS ? (
                  <div>
                    <button
                      type="button"
                      onClick={addReminder}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '8px 14px',
                        border: `1.5px dashed ${T.blue}`,
                        color: T.blue,
                        borderRadius: R.sm,
                        fontSize: 13,
                        fontWeight: 600,
                        background: T.blueBg,
                        cursor: 'pointer',
                        fontFamily: FONT,
                      }}
                    >
                      + Add reminder email
                    </button>
                  </div>
                ) : (
                  <div style={formHint}>
                    Up to {MAX_REMINDERS + 1} emails (Email 1 and{' '}
                    {MAX_REMINDERS} reminders). More than this usually leads to
                    unsubscribes.
                  </div>
                )}
                <div style={formHint}>
                  Each reminder is counted from when the email before it was
                  sent — e.g. Email 3 goes out its Resend after time after Email
                  2 — and only if none of the exclusions below have happened.
                </div>
              </Stack>
            ) : (
              <div style={{ ...formHint, marginTop: 0 }}>
                Off — only Email 1 is sent.
              </div>
            )}
          </StepCard>
        )}

        <StepCard
          title="Exclusions"
          icon={<Num n={showReminders ? 3 : 2} />}
          subtitle="Stop sending as soon as any of these happen"
        >
          <Stack gap={8}>
            <ExitCheck
              on={state.exitOnOpen}
              title="Customer opens the email"
              desc="They have seen the reminder, so no more emails are sent."
              onToggle={() => set('exitOnOpen', !state.exitOnOpen)}
            />
            <ExitCheck
              on={state.exitOnVisit}
              title="Customer visits the site"
              desc="They came back on their own, so reminders stop."
              onToggle={() => set('exitOnVisit', !state.exitOnVisit)}
            />
            <ExitCheck
              on
              locked
              title="Customer places an order"
              desc="Stops everything as soon as they buy."
            />
          </Stack>
        </StepCard>

        {frequencyCard(showReminders ? 4 : 3)}
      </Stack>
    )
  }

  return (
    <LockedFieldset disabled={scheduleLocked}>
      <Stack>
        {scheduleLocked && (
          <NoteBanner>
            Scheduling is managed from the Campaigns list (Schedule / Pause /
            Resume) — shown here for reference only.
          </NoteBanner>
        )}
        <SectionLabel>When do you want to send this campaign?</SectionLabel>
        <StepCard title="Send Time">
          <div
            role="radiogroup"
            aria-label="Send time"
            style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
          >
            <RadioOpt
              selected={state.scheduleMode === 'now'}
              label="Send immediately"
              desc="Campaign goes out as soon as you launch"
              onSelect={() => set('scheduleMode', 'now')}
            />
            <RadioOpt
              selected={state.scheduleMode === 'fixed'}
              label="Schedule for later"
              desc="Choose a specific date and time"
              onSelect={() => set('scheduleMode', 'fixed')}
            />
          </div>

          {state.scheduleMode === 'smart' && (
            <div style={{ marginTop: 14 }}>
              <NoteBanner tone="purple">
                This campaign uses smart send time — each recipient gets it at
                their best hour within 24 hours of launch.
                {!scheduleLocked &&
                  ' Smart send time is no longer available for new launches — pick an option above before launching.'}
              </NoteBanner>
            </div>
          )}

          {state.scheduleMode === 'fixed' && (
            <div
              style={{
                marginTop: 16,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 12,
              }}
            >
              <div>
                <label style={formLabel} htmlFor="cb-date">
                  Date
                </label>
                <input
                  id="cb-date"
                  style={formInput}
                  type="date"
                  value={datePart}
                  onChange={(e) =>
                    set(
                      'scheduledAt',
                      e.target.value
                        ? composeScheduledAt(e.target.value, timePart)
                        : '',
                    )
                  }
                />
              </div>
              <div>
                <label style={formLabel} htmlFor="cb-time">
                  Time ({timeZoneAbbr()})
                </label>
                <input
                  id="cb-time"
                  style={formInput}
                  type="time"
                  value={timePart || DEFAULT_SEND_TIME}
                  onChange={(e) =>
                    set(
                      'scheduledAt',
                      composeScheduledAt(datePart, e.target.value),
                    )
                  }
                />
              </div>
              {state.scheduledAt && !scheduledAtValid && !scheduleLocked && (
                <div style={{ ...formError, gridColumn: '1 / -1' }}>
                  Pick a date &amp; time in the future
                </div>
              )}
            </div>
          )}

          <div
            style={{
              marginTop: 18,
              paddingTop: 18,
              borderTop: `1px solid ${T.gray100}`,
            }}
          >
            <label style={formLabel} htmlFor="cb-ends">
              Campaign ends (optional)
            </label>
            <input
              id="cb-ends"
              style={{ ...formInput, maxWidth: 280 }}
              type="datetime-local"
              value={state.endsAt}
              onChange={(e) => set('endsAt', e.target.value)}
            />
            {state.endsAt && !endsAtValid && (
              <div style={formError}>Must be after the send time</div>
            )}
            <div style={formHint}>
              Shown as the campaign's date range on the calendar — it does not
              change when the campaign sends.
            </div>
          </div>
        </StepCard>
      </Stack>
    </LockedFieldset>
  )
}
